import {
  QueryFunction,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from 'react-query'
import {api} from '../services/api'
import {ResourceID, Segment} from '../types'
import {useRequiredAuth} from '../stores/AuthStore'

type Client = ReturnType<typeof useRequiredAuth>['client']
type Params = {client: Client; segmentID: ResourceID | null}
type GetQueryOptions = (params: Params) => UseQueryOptions<Segment>
type QueryFn = (params: Params) => QueryFunction<Segment>

// A segment attached to an existing micropost is immutable — the compose modal
// cannot change a published post's audience — so it never needs revalidating.
// cacheTime is pinned alongside staleTime because react-query's 5-minute
// default GC would evict a scrolled-past post's segment and silently defeat an
// infinite staleTime, refetching on the next render.
const QUERY_STALE_TIME = Infinity
const QUERY_CACHE_TIME = 1000 * 60 * 60 // 1 hour

const queryKeys = {
  all: () => [{resource: 'segments'}] as const, // [{ resource: 'segments'}]
  detail: ({segmentID}: {segmentID: ResourceID | null}) =>
    [{...queryKeys.all()[0], type: 'detail', segmentID}] as const, // [{ resource: 'segments', type: 'detail', segmentID: '456'}]
}

const queryFn: QueryFn =
  ({client, segmentID}) =>
  async () => {
    // Unreachable in practice — `enabled` gates the query on this same value.
    if (!segmentID) throw new Error('useSegment ran without a segment ID')

    const response = await api.segments.getSegment({
      clientID: client.ID,
      ID: segmentID,
    })

    // The api layer never throws; it returns a failure envelope. Re-throw so
    // react-query records an error, rather than caching a permanent "success"
    // whose data is missing.
    if (response.result !== 'success') throw new Error(response.error.message)

    return response.data
  }

const getQueryOptions: GetQueryOptions = ({client, segmentID}) => ({
  queryKey: queryKeys.detail({segmentID}),
  queryFn: queryFn({client, segmentID}),
  // An "Everyone" post carries no segment, so it issues no request at all.
  enabled: Boolean(segmentID),
  staleTime: QUERY_STALE_TIME,
  cacheTime: QUERY_CACHE_TIME,
})

type UseSegment = (params: {
  segmentID: ResourceID | null
}) => UseQueryResult<Segment>

/**
 * Reads one segment's stored filter content by ID.
 *
 * A single `useQuery` rather than the paged `useInfiniteQuery` its sibling
 * hooks use: the list endpoint cannot serve these rows at all. `GET /v0/segments`
 * filters on `Name IS NOT NULL` and micropost segments are created anonymously,
 * so a per-ID read is the only way in. react-query dedupes by key, so N posts
 * sharing a segment cost one request.
 */
export const useSegment: UseSegment = ({segmentID}) => {
  const {client} = useRequiredAuth()

  return useQuery<Segment>(getQueryOptions({client, segmentID}))
}
